// Combined filter modal for the ClipsBrowser (experience builder page):
// a single sliding panel with a category sidebar (Intents, Artist, Modalities,
// Duration, Difficulty, Moments) and a right-hand pane with that category's
// options, one shared "Clear" link, and one "Apply Filter" button.

import {
  type ComponentType,
  type FunctionComponent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { BaseModal, time } from '@dr.pogodin/react-utils';

import SliderStepInput from 'components/SliderStepInput';
import Throbber from 'components/Throbber';

import { useArtistList } from 'hooks';

import {
  DIFICULTIES,
  type DifficultyT,
  type FilterT,
  MODALITIES,
  type ModalityT,
  VIDEO_INTENTS,
  type VideoIntentT,
  areEqualFilters,
  cloneFilter,
  getId,
  isAllSelected,
} from 'utils';

import IconArtist from 'assets/images/ai/filters/filter-artist.svg';

import IconDifficultyAdvanced from 'assets/images/ai/filters/filter-difficulity-advanced.svg';
import IconDifficultyBeginner from 'assets/images/ai/filters/filter-difficulity-beginner.svg';
import IconDifficultyIntermediate from 'assets/images/ai/filters/filter-difficulity-intermediate.svg';
import IconDifficulty from 'assets/images/ai/filters/filter-difficulity.svg';
import IconDuration from 'assets/images/ai/filters/filter-duration.svg';
import IconIntentCalmAnxiety from 'assets/images/ai/filters/filter-intents-calm-anxiety.svg';
import IconIntentCreativityEnhancement from 'assets/images/ai/filters/filter-intents-creativity-enhancement.svg';
import IconIntentEmotionalReset from 'assets/images/ai/filters/filter-intents-emotional-rest.svg';
import IconIntentEnergyActivation from 'assets/images/ai/filters/filter-intents-energy-activation.svg';
import IconIntentMuscleRecovery from 'assets/images/ai/filters/filter-intents-muscle-recovery.svg';
import IconIntentSelfConnection from 'assets/images/ai/filters/filter-intents-self-connection.svg';
import IconIntentStressRelief from 'assets/images/ai/filters/filter-intents-stress-relief.svg';
import IconIntents from 'assets/images/ai/filters/filter-intents.svg';
import IconModalityBreathwork from 'assets/images/ai/filters/filter-modalities-breathwork.svg';
import IconModalityCalisthenics from 'assets/images/ai/filters/filter-modalities-calisthenics.svg';
import IconModalityFunctionalMovement from 'assets/images/ai/filters/filter-modalities-functional-movement.svg';
import IconModalityQiGongTaiChi from 'assets/images/ai/filters/filter-modalities-giqong-taichi.svg';
import IconModalityMeditation from 'assets/images/ai/filters/filter-modalities-meditation.svg';
import IconModalityOther from 'assets/images/ai/filters/filter-modalities-other.svg';
import IconModalityPilates from 'assets/images/ai/filters/filter-modalities-pilates.svg';
import IconModalitySomatic from 'assets/images/ai/filters/filter-modalities-somatic.svg';
import IconModalitySoundbath from 'assets/images/ai/filters/filter-modalities-soundbatch.svg';
import IconModalityYoga from 'assets/images/ai/filters/filter-modalities-yoga.svg';
import IconModalities from 'assets/images/ai/filters/filter-modalities.svg';
import IconMomentBreakTime from 'assets/images/ai/filters/filter-moments-break-time.svg';
import IconMomentDifficultConversation from 'assets/images/ai/filters/filter-moments-difficult-conversation.svg';
import IconMomentEveningTime from 'assets/images/ai/filters/filter-moments-evening-time.svg';
import IconMomentEveningTransition from 'assets/images/ai/filters/filter-moments-evening-transition.svg';
import IconMomentFamilyTime from 'assets/images/ai/filters/filter-moments-family-time.svg';
import IconMomentFocusTime from 'assets/images/ai/filters/filter-moments-focus-time.svg';
import IconMomentLearningSession from 'assets/images/ai/filters/filter-moments-learning-session.svg';
import IconMomentMeeting from 'assets/images/ai/filters/filter-moments-meeting.svg';
import IconMomentPresentation from 'assets/images/ai/filters/filter-moments-presentation.svg';
import IconMomentReflectionTime from 'assets/images/ai/filters/filter-moments-reflection-time.svg';
import IconMomentStartingMyDay from 'assets/images/ai/filters/filter-moments-starting-my-day.svg';
import IconMomentWorkSession from 'assets/images/ai/filters/filter-moments-work-session.svg';
import IconMomentWorkoutSession from 'assets/images/ai/filters/filter-moments-workout-session.svg';
import IconMoments from 'assets/images/ai/filters/filter-moments.svg';
import IconChevron from 'assets/images/icon-chevron-20px-b.svg';
import IconCross from 'assets/images/icon-cross-24px.svg';

import ArtistRow from './ArtistRow';
import OptionRow from './OptionRow';

import modalTheme from './modal.scss';

import './style.scss';

const INF = Number.MAX_SAFE_INTEGER;

const DURATION_STOPS: Array<[number, string]> = [
  [2, '2 min'],
  [4, '4 min'],
  [6, '6 min'],
  [8, '8 min'],
];

const INTENT_ICONS: Record<VideoIntentT, ComponentType> = {
  'Calm & Steadiness': IconIntentCalmAnxiety,
  'Confidence & Courage': IconIntentEmotionalReset,
  'Connection & Openness': IconIntentSelfConnection,
  'Energy & Readiness': IconIntentEnergyActivation,
  'Focus & Clarity': IconIntentCreativityEnhancement,
  'Reflection & Direction': IconIntentStressRelief,
  'Release & Recovery': IconIntentMuscleRecovery,
};

const MODALITY_ICONS: Record<ModalityT, ComponentType> = {
  Breathwork: IconModalityBreathwork,
  Calisthenics: IconModalityCalisthenics,
  'Functional Movement': IconModalityFunctionalMovement,
  Meditation: IconModalityMeditation,
  Other: IconModalityOther,
  Pilates: IconModalityPilates,
  'QiGong/Tai Chi': IconModalityQiGongTaiChi,
  'Somatic Movement': IconModalitySomatic,
  Soundbath: IconModalitySoundbath,
  Yoga: IconModalityYoga,
};

const DIFFICULTY_FILTER_ICONS: Record<DifficultyT, ComponentType> = {
  Advanced: IconDifficultyAdvanced,
  Beginner: IconDifficultyBeginner,
  Intermediate: IconDifficultyIntermediate,
};

// NOTE: "Moments" is a new, UI-only filter category for now: there is no
// backing video/experience data field for it yet (it is not the same thing
// as ExperienceT.lifeMomentId, whose 5 values don't match this 13-value set).
// It behaves like the other categories locally (selectable, clearable), but
// selections here are not sent to the videos query until the data model is
// settled.
const MOMENTS = [
  'Starting My Day',
  'Work Session',
  'Focus Time',
  'Learning Session',
  'Meeting',
  'Presentation',
  'Difficult Conversation',
  'Break Time',
  'Workout Session',
  'Family Time',
  'Evening Time',
  'Evening Transition',
  'Reflection Time',
] as const;

type MomentT = typeof MOMENTS[number];

const MOMENT_ICONS: Record<MomentT, ComponentType> = {
  'Break Time': IconMomentBreakTime,
  'Difficult Conversation': IconMomentDifficultConversation,
  'Evening Time': IconMomentEveningTime,
  'Evening Transition': IconMomentEveningTransition,
  'Family Time': IconMomentFamilyTime,
  'Focus Time': IconMomentFocusTime,
  'Learning Session': IconMomentLearningSession,
  Meeting: IconMomentMeeting,
  Presentation: IconMomentPresentation,
  'Reflection Time': IconMomentReflectionTime,
  'Starting My Day': IconMomentStartingMyDay,
  'Work Session': IconMomentWorkSession,
  'Workout Session': IconMomentWorkoutSession,
};

type CategoryT = 'artist'
  | 'difficulty'
  | 'duration'
  | 'intent'
  | 'modality'
  | 'moments';

type CategoryMetaT = { Icon: ComponentType; key: CategoryT; label: string };

const CATEGORIES: CategoryMetaT[] = [
  { Icon: IconIntents, key: 'intent', label: 'Intents' },
  { Icon: IconArtist, key: 'artist', label: 'Artist' },
  { Icon: IconModalities, key: 'modality', label: 'Modalities' },
  { Icon: IconDuration, key: 'duration', label: 'Duration' },
  { Icon: IconDifficulty, key: 'difficulty', label: 'Difficulty' },
  { Icon: IconMoments, key: 'moments', label: 'Moments' },
];

// Toggles a single option within a category's local (always-concrete, never
// `undefined`) selection object: `{}` means nothing is checked.
function toggle<T extends string>(filter: FilterT<T>, value: T): FilterT<T> {
  const next: Partial<Record<T, true>> = { ...filter };
  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
  if (next[value]) delete next[value];
  else next[value] = true;
  return next;
}

type ArtistFilterT = {
  initial: FilterT<string>;
  onChanged: (value: FilterT<string>) => void;
};

type DifficultyFilterT = {
  initial: FilterT<DifficultyT>;
  onChanged: (value: FilterT<DifficultyT>) => void;
};

type DurationFilterT = {
  initial: [number, number] | undefined;
  onChanged: (value: [number, number] | undefined) => void;
};

type IntentFilterT = {
  initial: FilterT<VideoIntentT>;
  onChanged: (value: FilterT<VideoIntentT>) => void;
};

type ModalityFilterT = {
  initial: FilterT<ModalityT>;
  onChanged: (value: FilterT<ModalityT>) => void;
};

type PropsT = {
  artist: ArtistFilterT;
  difficulty: DifficultyFilterT;
  duration: DurationFilterT;
  intent: IntentFilterT;
  modality: ModalityFilterT;
  onClose: () => void;
};

export const FilterModal: FunctionComponent<PropsT> = ({
  artist,
  difficulty,
  duration,
  intent,
  modality,
  onClose,
}) => {
  const [mounted, setMounted] = useState(false);
  const [category, setCategory] = useState<CategoryT>('intent');

  // Which category's accordion item is expanded, on mobile. Unlike `category`
  // (desktop's always-one-selected side nav), all items start collapsed.
  const [mobileExpanded, setMobileExpanded] = useState<CategoryT>();

  // Local per-category selections are always concrete objects (never
  // `undefined`), so every option starts out unchecked by default: `{}` means
  // nothing is checked, regardless of whether a filter was applied before.
  const [localArtist, setLocalArtist] = useState<FilterT<string>>(
    artist.initial ?? {},
  );
  const [localDifficulty, setLocalDifficulty] = useState<FilterT<DifficultyT>>(
    difficulty.initial ?? {},
  );
  const [localDuration, setLocalDuration] = useState(duration.initial);
  const [localIntent, setLocalIntent] = useState<FilterT<VideoIntentT>>(
    intent.initial ?? {},
  );
  const [localModality, setLocalModality] = useState<FilterT<ModalityT>>(
    modality.initial ?? {},
  );

  // UI-only for now, see the NOTE above MOMENTS.
  const [localMoments, setLocalMoments] = useState<FilterT<MomentT>>({});

  const artists = useArtistList({ status: 'Active' });
  const allArtistIds = artists.items.map(getId);
  const artistListRef = useRef<HTMLDivElement>(null);
  const loadTriggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTimeout(() => {
      setMounted(true);
    });
  }, []);

  const { loadMore } = artists;
  useEffect(() => {
    let observer: IntersectionObserver | undefined;
    if (loadMore && artistListRef.current && loadTriggerRef.current) {
      observer = new IntersectionObserver(([e]) => {
        if (e?.isIntersecting) loadMore();
      }, { root: artistListRef.current });
      observer.observe(loadTriggerRef.current);
    }
    return () => {
      observer?.disconnect();
    };
  }, [loadMore, category]);

  const onCloseWithAnimation = useCallback(() => {
    setMounted(false);

    // BEWARE: This timeout should match the animation duration used in SCSS.
    setTimeout(onClose, 0.3 * time.SEC_MS);
  }, [onClose]);

  let theme = modalTheme;
  if (mounted) {
    theme = { ...modalTheme };
    theme.container += ` ${modalTheme.mounted}`;
    theme.overlay += ` ${modalTheme.mounted}`;
  }

  // Whether every option of a category is currently checked. Duration has no
  // such concept (it's a continuous range, not a list of options).
  const isCategoryFull = (cat: CategoryT): boolean => {
    switch (cat) {
      case 'artist': return isAllSelected(localArtist, allArtistIds);
      case 'difficulty': return isAllSelected(localDifficulty, DIFICULTIES);
      case 'duration': return false;
      case 'intent': return isAllSelected(localIntent, VIDEO_INTENTS);
      case 'modality': return isAllSelected(localModality, MODALITIES);
      case 'moments': return isAllSelected(localMoments, MOMENTS);
      default: throw Error('Unexpected case');
    }
  };

  // The shared "All" / "Clear" link, used identically by both the desktop
  // panel and the mobile accordion: "All" selects every option in the
  // category; once everything is selected it flips to "Clear", which
  // unselects everything. Duration always shows "Clear" (reset the range).
  const getToggleLabel = (cat: CategoryT): string => (
    cat !== 'duration' && isCategoryFull(cat) ? 'Clear' : 'All'
  );

  const toggleCategorySelection = (cat: CategoryT) => {
    if (cat === 'duration') {
      setLocalDuration(undefined);
      return;
    }

    const full = isCategoryFull(cat);
    switch (cat) {
      case 'artist':
        setLocalArtist(full ? {} : cloneFilter(undefined, allArtistIds));
        break;
      case 'difficulty':
        setLocalDifficulty(full ? {} : cloneFilter(undefined, DIFICULTIES));
        break;
      case 'intent':
        setLocalIntent(full ? {} : cloneFilter(undefined, VIDEO_INTENTS));
        break;
      case 'modality':
        setLocalModality(full ? {} : cloneFilter(undefined, MODALITIES));
        break;
      case 'moments':
        setLocalMoments(full ? {} : cloneFilter(undefined, MOMENTS));
        break;
      default: throw Error('Unexpected case');
    }
  };

  // Collapses a fully-selected category back to `undefined` ("no filter"),
  // matching the convention used elsewhere for "everything matches".
  const finalize = <T extends string>(
    filter: FilterT<T>,
    all: readonly T[],
  ): FilterT<T> => (isAllSelected(filter, all) ? undefined : filter);

  const onApply = () => {
    const finalArtist = finalize(localArtist, allArtistIds);
    if (!areEqualFilters(artist.initial, finalArtist)) {
      artist.onChanged(finalArtist);
    }
    const finalDifficulty = finalize(localDifficulty, DIFICULTIES);
    if (!areEqualFilters(difficulty.initial, finalDifficulty)) {
      difficulty.onChanged(finalDifficulty);
    }
    const finalIntent = finalize(localIntent, VIDEO_INTENTS);
    if (!areEqualFilters(intent.initial, finalIntent)) {
      intent.onChanged(finalIntent);
    }
    const finalModality = finalize(localModality, MODALITIES);
    if (!areEqualFilters(modality.initial, finalModality)) {
      modality.onChanged(finalModality);
    }

    const initialMin = duration.initial?.[0] ?? 0;
    const initialMax = duration.initial?.[1] ?? INF;
    const localMin = localDuration?.[0] ?? 0;
    const localMax = localDuration?.[1] ?? INF;
    if (initialMin !== localMin || initialMax !== localMax) {
      duration.onChanged(localDuration);
    }

    onCloseWithAnimation();
  };

  const artistNodes: ReactNode[] = [];
  for (const a of artists.items) {
    const id = getId(a);
    artistNodes.push(
      <ArtistRow
        artist={a}
        checked={!!localArtist?.[id]}
        key={id}
        onSwitch={() => {
          setLocalArtist(toggle(localArtist, id));
        }}
      />,
    );
  }
  if (artists.loadMore) {
    artistNodes.push(<div key="load-trigger" ref={loadTriggerRef} />);
  }
  if (artists.loading || artists.loadMore) {
    artistNodes.push(
      <div key="throbber" styleName="throbberContainer"><Throbber /></div>,
    );
  }

  const getCategoryContent = (cat: CategoryT): ReactNode => {
    switch (cat) {
      case 'artist':
        return <div ref={artistListRef} styleName="optionsList">{artistNodes}</div>;
      case 'difficulty':
        return (
          <div styleName="optionsList">
            {DIFICULTIES.map((d) => (
              <OptionRow
                Icon={DIFFICULTY_FILTER_ICONS[d]}
                checked={!!localDifficulty?.[d]}
                key={d}
                label={d}
                onSwitch={() => {
                  setLocalDifficulty(toggle(localDifficulty, d));
                }}
              />
            ))}
          </div>
        );
      case 'duration':
        return (
          <div styleName="durationPanel">
            <p styleName="durationInfo">
              Pick a duration for your modality, anywhere between 2 and 8
              minutes!
            </p>
            <h3 styleName="durationLabel">Minimum</h3>
            <SliderStepInput
              light
              onChange={(value) => {
                let max = localDuration?.[1] ?? INF;
                if (max < value) max = value;
                setLocalDuration([value, max]);
              }}
              stops={DURATION_STOPS}
              value={localDuration?.[0] ?? 0}
            />
            <h3 styleName="durationLabel">Maximum</h3>
            <SliderStepInput
              light
              onChange={(value) => {
                let min = localDuration?.[0] ?? 0;
                if (min > value) min = value;
                setLocalDuration([min, value]);
              }}
              stops={DURATION_STOPS}
              value={localDuration?.[1] ?? INF}
            />
          </div>
        );
      case 'intent':
        return (
          <div styleName="optionsList">
            {VIDEO_INTENTS.map((v) => (
              <OptionRow
                Icon={INTENT_ICONS[v]}
                checked={!!localIntent?.[v]}
                key={v}
                label={v}
                onSwitch={() => {
                  setLocalIntent(toggle(localIntent, v));
                }}
              />
            ))}
          </div>
        );
      case 'modality':
        return (
          <div styleName="optionsList">
            {MODALITIES.map((m) => (
              <OptionRow
                Icon={MODALITY_ICONS[m]}
                checked={!!localModality?.[m]}
                key={m}
                label={m}
                onSwitch={() => {
                  setLocalModality(toggle(localModality, m));
                }}
              />
            ))}
          </div>
        );
      case 'moments':
        return (
          <div styleName="optionsList">
            {MOMENTS.map((m) => (
              <OptionRow
                Icon={MOMENT_ICONS[m]}
                checked={!!localMoments?.[m]}
                key={m}
                label={m}
                onSwitch={() => {
                  setLocalMoments(toggle(localMoments, m));
                }}
              />
            ))}
          </div>
        );
      default: throw Error('Unexpected case');
    }
  };

  const activeCategory = CATEGORIES.find(({ key }) => key === category);
  if (!activeCategory) throw Error('Missing active category');

  return (
    <BaseModal onCancel={onCloseWithAnimation} theme={theme}>
      <div styleName="layout">
        <div styleName="sidebar">
          <h2 styleName="sidebarTitle">Filter</h2>
          <nav styleName="nav">
            {CATEGORIES.map(({ Icon, key, label }) => (
              <button
                key={key}
                onClick={() => {
                  setCategory(key);
                }}
                styleName={key === category ? 'navItem active' : 'navItem'}
                type="button"
              >
                <Icon styleName="navIcon" />
                {label}
              </button>
            ))}
          </nav>
        </div>
        <div styleName="panel">
          <div styleName="panelHeader">
            <div styleName="panelTitle">
              <activeCategory.Icon styleName="panelIcon" />
              {activeCategory.label}
            </div>
            <button
              onClick={onCloseWithAnimation}
              styleName="closeButton"
              type="button"
            >
              <IconCross />
            </button>
          </div>
          <div styleName="panelScroll">
            <div
              onClick={() => {
                toggleCategorySelection(category);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') toggleCategorySelection(category);
              }}
              role="button"
              styleName="clearLink"
              tabIndex={0}
            >
              {getToggleLabel(category)}
            </div>
            {getCategoryContent(category)}
          </div>
          <button
            onClick={onApply}
            styleName="applyButton"
            type="button"
          >
            Apply Filter
          </button>
        </div>
      </div>

      <div styleName="mobileFilter">
        <div styleName="mobileHeader">
          <h2 styleName="mobileTitle">Filter</h2>
          <button
            onClick={onCloseWithAnimation}
            styleName="closeButton"
            type="button"
          >
            <IconCross />
          </button>
        </div>
        <div styleName="accordionScroll">
          {CATEGORIES.map(({ Icon, key, label }) => {
            const isOpen = mobileExpanded === key;
            return (
              <div key={key} styleName="accordionItem">
                <button
                  onClick={() => {
                    setMobileExpanded(isOpen ? undefined : key);
                  }}
                  styleName="accordionHeader"
                  type="button"
                >
                  <Icon styleName="accordionIcon" />
                  <span styleName="accordionLabel">{label}</span>
                  <IconChevron styleName={isOpen ? 'chevron open' : 'chevron'} />
                </button>
                {isOpen ? (
                  <div styleName="accordionBody">
                    <div
                      onClick={() => {
                        toggleCategorySelection(key);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') toggleCategorySelection(key);
                      }}
                      role="button"
                      styleName="selectAllLink"
                      tabIndex={0}
                    >
                      {getToggleLabel(key)}
                    </div>
                    {getCategoryContent(key)}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
        <button
          onClick={onApply}
          styleName="mobileApplyButton"
          type="button"
        >
          Apply Filter
        </button>
      </div>
    </BaseModal>
  );
};
